import {useCallback, useRef} from 'react'
import {tripService} from '@/services/tripService'
import {pickYyzArrivalPickup, resolveYyzTerminal} from '@/features/navigation/data/yyzArrivalEntries'
import {tripPickupPoint} from '@/features/fromAirportArrival/arrivalEligibility'

/**
 * "I'VE ARRIVED" — telling the driver the passenger is standing at the booth.
 *
 * ── ⚠️ THERE IS NO ARRIVAL ENDPOINT. THIS IS A LOCATION PING. ─────────────────────
 * The passenger API has 27 trip routes and not one of them announces an arrival; the only
 * `/arrive` in the whole system is the driver's own waypoint call. So both mobile apps do
 * the same thing this does: POST the pickup booth's coordinate to the ordinary location
 * endpoint. The backend relays it as `passenger.location.updated`, the driver's app
 * measures the distance from that position to the nearest known booth, gets 0 m, and
 * flips its screen to "{name} has arrived".
 *
 * Consequences worth knowing before changing anything here:
 *
 *  • The server cannot tell this from a real GPS fix. Nothing marks it — no `arrived`, no
 *    `source`, no `event`. Only `accuracy_m: 0` on an exact booth value hints at it.
 *  • It is OVERWRITTEN by the next real fix. If live sharing is running, the driver's copy
 *    of the passenger's position goes back to the true one within about a second. The
 *    driver's UI has usually latched by then, but it is a race, which is why this sends
 *    more than once.
 *  • Nothing persists it. There is no read-back and no history; a dropped ping is simply
 *    gone.
 *
 * ── ⚠️ AND THE FAILURE IS INVISIBLE ───────────────────────────────────────────────
 * The location endpoint swallows its errors by design — a 422 from a status guard, a 401,
 * a dead network all look the same to a caller. So there is no "arrival failed" to show.
 * Sending three times, spaced, is the honest answer to a channel that cannot be
 * acknowledged: it costs three cheap requests and covers a transient drop.
 */

/**
 * WHEN THE THREE GO.
 *
 * ⚠️ SPACED, NOT BURSTED. Three requests in the same millisecond share one network moment
 * and fail together; a second apart they cover a handover, a lift, a tunnel. It matches
 * the mobile app's own arrival button.
 */
const RESEND_DELAYS_MS = [0, 1200, 2400]

/**
 * HOW LONG A PRESS SUPPRESSES THE NEXT ONE.
 *
 * ⚠️ IT IS A DOUBLE-TAP GUARD, NOT A RATE LIMIT. A passenger whose driver has not set off
 * is meant to be able to press again — that is what the button is for the second time. The
 * window only has to outlast the three sends it already started.
 */
const REPRESS_GUARD_MS = 3000

/**
 * @param {object|null} trip
 * @returns {{announce: () => boolean}} `announce` returns false when it sent nothing —
 *   either it has no coordinate to send or the guard is still up.
 */
export function useAnnounceArrival(trip) {
    const lastSentAt = useRef(0)
    const timers = useRef([])

    const announce = useCallback(() => {
        const tripId = trip?.id
        if (!tripId) return false

        const now = Date.now()
        if (now - lastSentAt.current < REPRESS_GUARD_MS) return false

        const point = arrivalCoordinate(trip)
        if (!point) return false

        lastSentAt.current = now
        timers.current.forEach(clearTimeout)
        timers.current = RESEND_DELAYS_MS.map((delay) => setTimeout(() => {
            /* ⚠️ NOT AWAITED, AND NOTHING READS THE RESULT. See the note above: the
               endpoint reports nothing a caller could act on, and a passenger who has
               arrived should not be held on a spinner while three fire-and-forget pings
               resolve. */
            tripService.submitLocation(tripId, {lat: point.lat, lng: point.lng, accuracy_m: 0})
        }, delay))

        return true
    }, [trip])

    return {announce}
}

/**
 * WHICH COORDINATE TO SEND.
 *
 * ⚠️ THE BOOTH BEATS THE TRIP'S OWN PICKUP, and that is not a preference. The driver's
 * side decides "has my passenger arrived" by measuring against ITS copy of these same
 * surveyed booths, within 50 m. The trip's pickup is a geocoded terminal address that can
 * sit a couple of hundred metres from the booth — far enough to fail that test on a
 * passenger who is standing exactly where they were told to.
 *
 * ⚠️ THE TRIP'S PICKUP IS STILL THE FALLBACK, unlike the mobile steps screen which sends
 * NOTHING when it does not recognise the terminal. Silence there means a passenger presses
 * "I've arrived" at an airport with no surveyed booths and the driver never hears. An
 * approximate position is worth more than none.
 */
function arrivalCoordinate(trip) {
    const pickup = tripPickupPoint(trip)
    const terminal = pickup ? resolveYyzTerminal([pickup.lng, pickup.lat]) : null

    if (terminal) {
        /* ⚠️ NO FLIGHT MEANS THE PREARRANGED BOOTH, not a guess at domestic. All three of a
           terminal's booths are within the driver's 50 m test of each other, so the
           fallback is safe; `pickYyzArrivalPickup` never returns null. */
        const [lng, lat] = pickYyzArrivalPickup(terminal, null)
        return {lat, lng}
    }

    return pickup || null
}

export default useAnnounceArrival
